import { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import api from '../services/api';
import { ReportJob } from '../types';

function Reports() {
  const { user: currentUser } = useUser();
  const [isRequestorModalOpen, setIsRequestorModalOpen] = useState(false);
  const [requestor, setRequestor] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateSuccess, setGenerateSuccess] = useState<string | null>(null);
  const [recentReports, setRecentReports] = useState<ReportJob[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadRecentReports();
    }
  }, [currentUser]);

  const loadRecentReports = async () => {
    if (!currentUser) return;

    setLoadingReports(true);
    try {
      const response = await api.get('/reports/jobs');
      setRecentReports(response.data || []);
    } catch (err: any) {
      console.error('Failed to load reports:', err);
      // Silently fail - reports might not be available yet
    } finally {
      setLoadingReports(false);
    }
  };

  const handleGenerateRequestorReport = async () => {
    if (!requestor.trim()) {
      setGenerateError('Please enter a requestor name');
      return;
    }

    if (!currentUser) {
      setGenerateError('You must be logged in to generate reports');
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);
    setGenerateSuccess(null);

    try {
      // Step 1: Create report job
      const createResponse = await api.post('/reports/jobs', {
        reportType: 'requestor',
        parameters: {
          requestor: requestor.trim()
        }
      });

      const reportJobId = createResponse.data.id;
      setGenerateSuccess('Report is being generated...');

      // Step 2: Process the report (generate Excel and upload to Azure)
      try {
        await api.post(`/reports/jobs/${reportJobId}/process`);
        
        // Success - refresh reports to show the new completed report
        await loadRecentReports();
        
        setGenerateSuccess('Report generated successfully!');
        setIsRequestorModalOpen(false);
        setRequestor('');
      } catch (processErr: any) {
        console.error('Failed to process report:', processErr);
        // Report job was created but processing failed - still show success for job creation
        await loadRecentReports();
        setGenerateError(
          'Report job created but processing failed. ' + 
          (processErr.response?.data?.message || 'Please try again or check the report status.')
        );
      }
    } catch (err: any) {
      console.error('Failed to generate report:', err);
      setGenerateError(err.response?.data?.message || 'Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadReport = async (reportJob: ReportJob) => {
    if (!reportJob.blobPath) {
      alert('Report file is not available yet');
      return;
    }

    try {
      // Generate SAS URL for download
      const response = await api.get(`/reports/jobs/${reportJob.id}/download`);
      const downloadUrl = response.data.url;
      
      // Open download link
      window.open(downloadUrl, '_blank');
    } catch (err: any) {
      console.error('Failed to download report:', err);
      alert('Failed to download report. Please try again.');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'processing':
        return '#ffc107';
      case 'failed':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  return (
    <div>
      <h1>Reports</h1>
      <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button 
          onClick={() => setIsRequestorModalOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: 'none',
            border: '1px solid #6c757d',
            color: '#6c757d',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontSize: '1rem',
            fontWeight: '600'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(108, 117, 125, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span className="material-symbols-outlined small">person</span>
          Generate Report by Requestor
        </button>
      </div>
      <div style={{ marginTop: '30px' }}>
        <h2>Recent Reports</h2>
        {loadingReports ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#6c757d', margin: 0 }}>Loading reports...</p>
          </div>
        ) : recentReports.length > 0 ? (
          <div className="card" style={{ padding: '0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Report Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Parameters</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Requested</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Completed</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentReports.map((report) => (
                  <tr key={report.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '12px' }}>{report.reportType}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        fontSize: '0.875rem'
                      }}>
                        {Object.entries(report.parameters || {}).map(([key, value]) => {
                          const labelMap: Record<string, string> = {
                            requestor: 'Requestor',
                            status: 'Status',
                            agencyId: 'Agency ID',
                            dateFrom: 'Date From',
                            dateTo: 'Date To',
                            vin: 'VIN',
                            clientNumber: 'Client Number',
                            unitNumber: 'Unit Number'
                          };
                          const label = labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                          const displayValue = value !== null && value !== undefined ? String(value) : '-';
                          
                          return (
                            <div key={key} style={{ display: 'flex', gap: '8px' }}>
                              <span style={{ fontWeight: '500', color: 'var(--secondary-color)' }}>
                                {label}:
                              </span>
                              <span style={{ color: '#495057' }}>
                                {displayValue}
                              </span>
                            </div>
                          );
                        })}
                        {(!report.parameters || Object.keys(report.parameters).length === 0) && (
                          <span style={{ color: '#6c757d', fontStyle: 'italic' }}>-</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ 
                        color: getStatusColor(report.status),
                        fontWeight: '500'
                      }}>
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{formatDate(report.createdAt)}</td>
                    <td style={{ padding: '12px' }}>{formatDate(report.completedAt)}</td>
                    <td style={{ padding: '12px' }}>
                      {report.status === 'completed' && report.blobPath && (
                        <button
                          onClick={() => handleDownloadReport(report)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            background: 'var(--primary-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.9';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>download</span>
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <span className="material-symbols-outlined large" style={{ color: '#6c757d', marginBottom: '16px' }}>description</span>
            <p style={{ color: '#6c757d', fontStyle: 'italic', margin: 0 }}>No reports generated yet</p>
          </div>
        )}
      </div>

      {/* Requestor Report Modal */}
      {isRequestorModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              borderBottom: '1px solid #e0e0e0',
              paddingBottom: '16px'
            }}>
              <h2 style={{ margin: 0, color: 'var(--secondary-color)' }}>
                Generate Report by Requestor
              </h2>
              <button
                onClick={() => {
                  setIsRequestorModalOpen(false);
                  setRequestor('');
                  setGenerateError(null);
                  setGenerateSuccess(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Close modal"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {generateError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {generateError}
              </div>
            )}

            {generateSuccess && (
              <div style={{
                backgroundColor: '#d4edda',
                color: '#155724',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {generateSuccess}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Requestor Name *
              </label>
              <input
                type="text"
                value={requestor}
                onChange={(e) => setRequestor(e.target.value)}
                placeholder="Enter requestor name"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isGenerating) {
                    handleGenerateRequestorReport();
                  }
                }}
              />
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '12px', 
                color: '#6c757d',
                fontStyle: 'italic'
              }}>
                Enter the requestor name to filter the report
              </p>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setIsRequestorModalOpen(false);
                  setRequestor('');
                  setGenerateError(null);
                  setGenerateSuccess(null);
                }}
                disabled={isGenerating}
                style={{
                  padding: '10px 20px',
                  background: 'none',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: isGenerating ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  color: '#6c757d',
                  opacity: isGenerating ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateRequestorReport}
                disabled={isGenerating || !requestor.trim()}
                style={{
                  padding: '10px 20px',
                  background: 'var(--primary-color)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (isGenerating || !requestor.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  color: 'white',
                  opacity: (isGenerating || !requestor.trim()) ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isGenerating && requestor.trim()) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isGenerating && requestor.trim()) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reports;


